/**
 * Get the Apps — the user's download page.
 *
 * The CCMS apps are distributed PRIVATELY: you cannot find them by searching the App Store
 * or Play Store. So this page is the only way in. It lists the apps that are LIVE and that
 * you are entitled to; tapping one opens a modal with everything needed to install it.
 *
 *  - iOS     — your redemption code, and a single "Install" button that opens the App Store
 *              with the code ALREADY FILLED IN
 *              (https://apps.apple.com/redeem?ctx=apps&code=…). Nobody types a code: one
 *              mistyped character wastes a single-use code.
 *  - Android — a plain install link. Google Play has no redemption codes for free apps.
 *
 * Codes are assigned by an admin — Apple issues a finite number of them.
 *
 * Route: /app/apps (protected — see App.tsx).
 */

import { useMemo, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  IconSearch,
  IconRefresh,
  IconExternalLink,
  IconMoodEmpty,
  IconCopy,
  IconCircleCheck,
  IconDownload,
  IconInfoCircle,
  IconTicket,
} from '@tabler/icons-react';
import {
  Stack,
  Card,
  Group,
  Button,
  Text,
  Title,
  TextInput,
  Badge,
  Paper,
  SimpleGrid,
  Avatar,
  Alert,
  Divider,
  Tooltip,
  ActionIcon,
  Tabs,
  toast,
} from '@/components/ui';
import { Modal } from '@/components/ui/overlays';
import { FadeIn, HoverLift } from '@/components/animations';
import { useAppDistributionService, APP_PLATFORM } from '@actschurch/ccms-sdk';
import type { MyAppInstall, MyAppPlatformInstall, MyAppCode } from '@actschurch/ccms-sdk';
import { buildRedeemLink, isRedeemLink } from '@/utils/appleRedeem';
import { AppleLogo, GooglePlayLogo } from '@/components/brand/PlatformLogos';

const MY_APPS_KEY = ['catalogue', 'me', 'apps'];

export default function AppsPage() {
  const queryClient = useQueryClient();
  const distributionService = useAppDistributionService();

  const [searchQuery, setSearchQuery] = useState('');
  const [selected, setSelected] = useState<MyAppInstall | null>(null);

  const {
    data: apps = [],
    isLoading,
    error,
    isFetching,
  } = useQuery({
    queryKey: MY_APPS_KEY,
    queryFn: () => distributionService.getMyApps(),
  });

  const rows = useMemo(() => {
    if (!searchQuery.trim()) return apps;
    const q = searchQuery.trim().toLowerCase();
    return apps.filter(
      (a) =>
        a.displayName.toLowerCase().includes(q) ||
        a.shortName.toLowerCase().includes(q) ||
        (a.description ?? '').toLowerCase().includes(q)
    );
  }, [apps, searchQuery]);

  const waiting = useMemo(() => countUnusedCodes(apps), [apps]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: MY_APPS_KEY });

  // Keep the open modal in step with a refetch — otherwise redeeming a code leaves the
  // modal showing the old state until you close and reopen it.
  const selectedLive = selected ? (apps.find((a) => a.appId === selected.appId) ?? null) : null;

  if (error) {
    return (
      <Stack gap="lg">
        <Title order={2}>Get the Apps</Title>
        <Alert color="red" title="Service unavailable">
          <Text size="sm">
            {error instanceof Error ? error.message : 'Failed to load your apps.'}
          </Text>
          <Button variant="light" color="red" size="xs" mt="sm" onClick={refresh}>
            Retry
          </Button>
        </Alert>
      </Stack>
    );
  }

  return (
    <FadeIn>
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={2}>Get the Apps</Title>
            <Text c="dimmed" size="sm">
              The CCMS apps are private — you won't find them by searching the store. Install
              them from here.
            </Text>
          </div>
          <Button
            variant="subtle"
            size="sm"
            leftSection={<IconRefresh size={16} />}
            onClick={refresh}
            loading={isFetching}
          >
            Refresh
          </Button>
        </Group>

        {waiting > 0 && (
          <Alert color="violet" icon={<IconTicket size={16} />}>
            <Text size="sm">
              You have {waiting} code{waiting === 1 ? '' : 's'} ready to use. Each one works once.
            </Text>
          </Alert>
        )}

        {apps.length > 4 && (
          <TextInput
            placeholder="Search apps…"
            leftSection={<IconSearch size={16} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.currentTarget.value)}
          />
        )}

        {isLoading ? (
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3, xl: 4 }} spacing="md">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} withBorder padding="md" radius="md">
                <div
                  style={{
                    height: 130,
                    background: 'var(--mantine-color-gray-1)',
                    borderRadius: 'var(--mantine-radius-sm)',
                  }}
                />
              </Card>
            ))}
          </SimpleGrid>
        ) : rows.length === 0 ? (
          <Paper withBorder p="xl" ta="center">
            <Stack gap="xs" align="center">
              <IconMoodEmpty size={32} color="var(--mantine-color-gray-5)" />
              <Text c="dimmed" maw={460}>
                {apps.length === 0
                  ? "No apps are available to you yet. Once an administrator publishes one you're entitled to, it will appear here."
                  : 'No apps match your search.'}
              </Text>
            </Stack>
          </Paper>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3, xl: 4 }} spacing="md">
            {rows.map((app) => (
              <AppTile key={app.appId} app={app} onOpen={() => setSelected(app)} />
            ))}
          </SimpleGrid>
        )}
      </Stack>

      <InstallModal
        app={selectedLive}
        onClose={() => setSelected(null)}
        onChanged={refresh}
      />
    </FadeIn>
  );
}

/** A compact tile. The detail lives in the modal — this just says "can I install it yet?". */
function AppTile({ app, onOpen }: { app: MyAppInstall; onOpen: () => void }) {
  const status = installStatus(app);

  return (
    <HoverLift lift={4}>
      <Card
        withBorder
        padding="md"
        radius="md"
        h="100%"
        style={{ cursor: 'pointer' }}
        onClick={onOpen}
      >
        <Stack gap="sm" h="100%">
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Avatar
              src={app.iconUrl || undefined}
              radius="md"
              size="lg"
              style={{ backgroundColor: app.colors?.primary || undefined }}
            >
              {app.shortName?.charAt(0) ?? '?'}
            </Avatar>
            <Badge size="sm" color={status.color} variant="light">
              {status.label}
            </Badge>
          </Group>

          <div>
            <Text fw={600} lineClamp={1}>
              {app.displayName}
            </Text>
            {app.description && (
              <Text size="xs" c="dimmed" lineClamp={2}>
                {app.description}
              </Text>
            )}
          </div>

          <div style={{ flex: 1 }} />

          {/* The store marks carry the same weight as the button: real logos, in bordered
              tiles the same height as Install. A user recognises "App Store" or "Play"
              from the mark alone — it does as much work as the button does, so it has no
              business being grey decoration beside it. */}
          <Group gap="xs" wrap="nowrap" align="stretch">
            <Button
              size="md"
              variant="light"
              leftSection={<IconDownload size={16} />}
              style={{ flex: 1 }}
            >
              {status.cta}
            </Button>

            {app.platforms.map((p) => (
              <Tooltip key={p.distributionId} label={`Available on ${p.platformName}`}>
                <Paper
                  withBorder
                  radius="md"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 42,
                    alignSelf: 'stretch',
                  }}
                >
                  <PlatformMark platformId={p.platformId} size={24} />
                </Paper>
              </Tooltip>
            ))}
          </Group>
        </Stack>
      </Card>
    </HoverLift>
  );
}

/** Everything needed to install one app, without leaving the page. */
function InstallModal({
  app,
  onClose,
  onChanged,
}: {
  app: MyAppInstall | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  return (
    <Modal
      opened={Boolean(app)}
      onClose={onClose}
      title={app?.displayName ?? ''}
      // Wide enough that the code, its download link and the instructions all sit on one
      // line each instead of wrapping. Capped at 92vw so it still fits a laptop screen.
      size="xl"
      styles={{ content: { maxWidth: '92vw' } }}
    >
      {app && (
        <Stack gap="lg">
          <Group gap="md" wrap="nowrap">
            <Avatar
              src={app.iconUrl || undefined}
              radius="md"
              size="lg"
              style={{ backgroundColor: app.colors?.primary || undefined }}
            >
              {app.shortName?.charAt(0) ?? '?'}
            </Avatar>
            <div style={{ minWidth: 0 }}>
              {app.description && (
                <Text size="sm" c="dimmed">
                  {app.description}
                </Text>
              )}
              {app.webUrl && (
                <Button
                  size="compact-xs"
                  variant="subtle"
                  mt={4}
                  leftSection={<IconExternalLink size={12} />}
                  component="a"
                  href={app.webUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open in your browser instead
                </Button>
              )}
            </div>
          </Group>

          {/* One platform at a time when there are two — you only have one phone in your
              hand, and stacking both made the modal a scroll. */}
          {app.platforms.length > 1 ? (
            <Tabs defaultValue={String(app.platforms[0].platformId)}>
              <Tabs.List grow>
                {app.platforms.map((p) => (
                  <Tabs.Tab
                    key={p.distributionId}
                    value={String(p.platformId)}
                    leftSection={<PlatformMark platformId={p.platformId} size={18} />}
                  >
                    {p.platformName}
                  </Tabs.Tab>
                ))}
              </Tabs.List>

              {app.platforms.map((p) => (
                <Tabs.Panel key={p.distributionId} value={String(p.platformId)} pt="md">
                  <PlatformSection
                    platform={p}
                    appName={app.displayName}
                    onChanged={onChanged}
                    showHeader={false}
                  />
                </Tabs.Panel>
              ))}
            </Tabs>
          ) : (
            app.platforms.map((platform) => (
              <PlatformSection
                key={platform.distributionId}
                platform={platform}
                appName={app.displayName}
                onChanged={onChanged}
              />
            ))
          )}
        </Stack>
      )}
    </Modal>
  );
}

/** One platform's install path: a code + one-tap redeem (iOS), or a link (Android). */
function PlatformSection({
  platform,
  appName,
  onChanged,
  showHeader = true,
}: {
  platform: MyAppPlatformInstall;
  appName: string;
  onChanged: () => void;
  /** False inside tabs — the tab already names the platform. */
  showHeader?: boolean;
}) {
  const isIos = platform.platformId === APP_PLATFORM.IOS;
  const usable = platform.codes.filter((c) => !c.isRedeemed);
  const used = platform.codes.filter((c) => c.isRedeemed);

  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="sm">
        {showHeader ? (
          <>
            <Group justify="space-between">
              <Group gap="xs">
                <PlatformMark platformId={platform.platformId} size={20} />
                <Text fw={600}>{platform.platformName}</Text>
              </Group>
              {platform.appVersion && (
                <Badge size="xs" variant="light" color="gray">
                  v{platform.appVersion}
                </Badge>
              )}
            </Group>
            <Divider />
          </>
        ) : (
          platform.appVersion && (
            <Group justify="flex-end">
              <Badge size="xs" variant="light" color="gray">
                v{platform.appVersion}
              </Badge>
            </Group>
          )
        )}

        {platform.yieldsCode ? (
          platform.codes.length > 0 ? (
            <Stack gap="md">
              {/* Unused first, but USED CODES STAY VISIBLE — with their code and their
                  redeem link. You may well need them again: reinstalling, restoring a
                  phone, or setting the app up on a second device. Hiding a code the moment
                  it's marked used just means having to ask an admin for it back. */}
              {[...usable, ...used].map((code) => (
                <CodeBlock
                  key={code.codeId}
                  code={code}
                  installUrl={platform.installUrl}
                  onChanged={onChanged}
                />
              ))}

              {usable.length === 0 && (
                <Alert color="green" icon={<IconCircleCheck size={16} />}>
                  <Text size="sm">
                    You've used your {appName} code. It should already be installed — check your
                    home screen. The code is kept above in case you need it again.
                  </Text>
                </Alert>
              )}

              {/* The app's STORE PAGE, separate from the per-code redeem link. Skipped when
                  the configured link is itself a redeem link — codeless, it opens a blank
                  redeem screen, which is exactly the dead end this used to be. */}
              {platform.installUrl && !isRedeemLink(platform.installUrl) && (
                <StoreLink platform={platform} />
              )}
            </Stack>
          ) : (
            <Stack gap="sm">
              <Alert color="gray" icon={<IconInfoCircle size={16} />}>
                <Text size="sm">
                  No code assigned yet. Ask your CCMS administrator for one
                  {platform.supportContact ? ` (${platform.supportContact})` : ''}.
                </Text>
              </Alert>
              {platform.installUrl && <StoreLink platform={platform} />}
            </Stack>
          )
        ) : (
          <Stack gap="sm">
            {platform.requiresTesterList && (
              <Alert color="yellow" icon={<IconInfoCircle size={16} />}>
                <Text size="sm">
                  Your {isIos ? 'Apple ID' : 'Google account'} must be on the tester list before
                  this link works. Ask your administrator
                  {platform.supportContact ? ` (${platform.supportContact})` : ''} if it doesn't.
                </Text>
              </Alert>
            )}

            {platform.installInstructions && (
              <Text size="sm" c="dimmed" style={{ whiteSpace: 'pre-line' }}>
                {platform.installInstructions}
              </Text>
            )}

            {platform.installUrl ? (
              <Button
                leftSection={<IconDownload size={16} />}
                component="a"
                href={platform.installUrl}
                target="_blank"
                rel="noopener noreferrer"
                fullWidth
              >
                Install on {platform.platformName}
              </Button>
            ) : (
              <Text size="sm" c="dimmed">
                The install link hasn't been set up yet.
              </Text>
            )}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}

/** The app's own store page — separate from the redeem link, which carries a code. */
function StoreLink({ platform }: { platform: MyAppPlatformInstall }) {
  const isIos = platform.platformId === APP_PLATFORM.IOS;

  const copy = async () => {
    await navigator.clipboard.writeText(platform.installUrl!);
    toast.success('Store link copied');
  };

  return (
    <Group gap="xs" wrap="nowrap">
      <Button
        variant="default"
        leftSection={<IconExternalLink size={16} />}
        component="a"
        href={platform.installUrl!}
        target="_blank"
        rel="noopener noreferrer"
        style={{ flex: 1 }}
      >
        View on {isIos ? 'the App Store' : 'Google Play'}
      </Button>
      <Tooltip label="Copy the store link">
        <ActionIcon variant="default" size="lg" onClick={copy}>
          <IconCopy size={16} />
        </ActionIcon>
      </Tooltip>
    </Group>
  );
}

/**
 * One redemption code.
 *
 * The primary action is NOT "copy the code" — it's the redeem link, which opens the App
 * Store with the code already entered. The code and the link are both copyable, because
 * you often want to install on a phone while looking at this on a laptop.
 *
 * A USED code stays here, in full, with its link. It is spent as far as the pool is
 * concerned, but the person who holds it may still need it — restoring a device,
 * reinstalling, or setting the app up again. Hiding it would only mean asking an admin to
 * read it back to them.
 */
function CodeBlock({
  code,
  installUrl,
  onChanged,
}: {
  code: MyAppCode;
  /** The distribution's configured link — used as the redeem template if it is one. */
  installUrl?: string | null;
  onChanged: () => void;
}) {
  const distributionService = useAppDistributionService();

  // The API sends `redeemUrl` per code. Build it here as well when it's absent, so the
  // download link is present even against an API that predates that field — it's pure
  // string construction from a code we already have, so there is nothing to wait for.
  const redeemUrl = code.redeemUrl ?? buildRedeemLink(code.codeValue, installUrl);

  const redeemMutation = useMutation({
    mutationFn: () => distributionService.redeemMyCode(code.codeId),
    onSuccess: () => {
      toast.success('Marked as used', 'Thanks — that keeps our records straight.');
      onChanged();
    },
    onError: (err: any) => {
      const message: string = err?.message ?? '';
      if (/409|already been used|revoked/i.test(message)) {
        toast.error('That code is already spent', 'It has been used or withdrawn.');
        onChanged();
      } else {
        toast.error('Could not mark it as used', message);
      }
    },
  });

  const copyValue = async (value: string, what: string) => {
    await navigator.clipboard.writeText(value);
    toast.success(`${what} copied`);
  };

  const expiresSoon =
    code.expiresAt && new Date(code.expiresAt).getTime() - Date.now() < 7 * 24 * 3600 * 1000;

  const used = code.isRedeemed;

  return (
    <Paper
      p="sm"
      radius="md"
      style={{
        background: used ? undefined : 'var(--mantine-color-violet-light)',
        border: used
          ? '1px solid var(--mantine-color-default-border)'
          : '1px dashed var(--mantine-color-violet-4)',
      }}
    >
      {/* Two columns on a wide modal — the code on the left, what you do with it on the
          right — so the extra width is actually used instead of everything stacking down
          the middle. Wraps back to one column on a narrow screen. */}
      <Group align="flex-start" wrap="wrap" gap="md">
        <Stack gap={4} style={{ flex: '1 1 200px', minWidth: 0 }}>
          <Group gap={6}>
            <Text size="xs" fw={600} c={used ? 'dimmed' : 'violet'} tt="uppercase">
              Your code
            </Text>
            {used && (
              <Badge size="xs" color="green" variant="light">
                Used
              </Badge>
            )}
          </Group>

          <Group gap="xs" wrap="nowrap">
            <Text
              ff="monospace"
              fw={700}
              size="xl"
              c={used ? 'dimmed' : undefined}
              style={{ letterSpacing: '0.08em', wordBreak: 'break-all' }}
            >
              {code.codeValue}
            </Text>
            <Tooltip label="Copy the code">
              <ActionIcon
                variant="light"
                color={used ? 'gray' : 'violet'}
                onClick={() => copyValue(code.codeValue, 'Code')}
              >
                <IconCopy size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>

          {code.expiresAt && (
            <Text size="xs" c={expiresSoon && !used ? 'red' : 'dimmed'}>
              {expiresSoon && !used ? 'Expires soon: ' : 'Expires '}
              {new Date(code.expiresAt).toLocaleDateString()}
            </Text>
          )}
        </Stack>

        <Stack gap="xs" style={{ flex: '1 1 300px', minWidth: 0 }}>
          {/* One tap, code pre-filled, nothing typed. Still offered on a USED code —
              reinstalling or restoring a phone is exactly when you need it again. */}
          {redeemUrl ? (
            <Group gap="xs" wrap="nowrap">
              <Button
                variant={used ? 'default' : 'filled'}
                leftSection={<IconDownload size={16} />}
                component="a"
                href={redeemUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ flex: 1 }}
              >
                {used ? 'Download again with this code' : 'Download with this code'}
              </Button>
              <Tooltip label="Copy the download link">
                <ActionIcon
                  variant={used ? 'default' : 'light'}
                  color={used ? 'gray' : 'violet'}
                  size="lg"
                  onClick={() => copyValue(redeemUrl, 'Download link')}
                >
                  <IconCopy size={16} />
                </ActionIcon>
              </Tooltip>
            </Group>
          ) : (
            <Text size="sm" c="dimmed">
              Open the App Store, tap your profile picture, then "Redeem Gift Card or Code",
              and enter the code above.
            </Text>
          )}

          {!used && (
            <Button
              variant="light"
              color="violet"
              leftSection={<IconCircleCheck size={16} />}
              onClick={() => redeemMutation.mutate()}
              loading={redeemMutation.isPending}
              fullWidth
            >
              I've installed it — mark used
            </Button>
          )}
        </Stack>
      </Group>
    </Paper>
  );
}

/** Which store an app comes from, as its real brand mark rather than a grey outline. */
function PlatformMark({ platformId, size = 22 }: { platformId: number; size?: number }) {
  return platformId === APP_PLATFORM.IOS ? (
    <AppleLogo size={size} />
  ) : (
    <GooglePlayLogo size={size} />
  );
}

function countUnusedCodes(apps: MyAppInstall[]): number {
  return apps.reduce(
    (sum, app) =>
      sum +
      app.platforms.reduce((p, plat) => p + plat.codes.filter((c) => !c.isRedeemed).length, 0),
    0
  );
}

/** What the tile says at a glance: can I install this right now, or am I waiting on someone? */
function installStatus(app: MyAppInstall): { label: string; color: string; cta: string } {
  const platforms = app.platforms;

  // No distribution configured at all — there is no mobile build to install, so this is
  // NOT "needs a code" (there is no code to want). Saying so would send people asking an
  // admin for something that doesn't exist. The API filters these out once redeployed;
  // until then, be honest about it.
  if (platforms.length === 0) {
    return { label: 'Web only', color: 'gray', cta: 'View' };
  }

  const hasUnusedCode = platforms.some((p) => p.codes.some((c) => !c.isRedeemed));
  const allUsed =
    platforms.some((p) => p.codes.length > 0) &&
    platforms.every((p) => p.codes.length === 0 || p.codes.every((c) => c.isRedeemed));

  if (hasUnusedCode) return { label: 'Code ready', color: 'violet', cta: 'Install' };
  if (allUsed) return { label: 'Installed', color: 'green', cta: 'View' };

  // Link-based platforms need no code at all.
  const linkOnly = platforms.some((p) => !p.yieldsCode && p.installUrl);
  if (linkOnly) return { label: 'Ready', color: 'blue', cta: 'Install' };

  return { label: 'Needs a code', color: 'gray', cta: 'View' };
}
